/**
 * Format phone number for display with international support
 * Uses country-specific formatting based on the phone number structure
 *
 * @param phone - Raw phone number (may include country code, special chars)
 * @param countryCode - ISO country code (e.g., 'us', 'ca', 'gb', 'in')
 * @returns Formatted phone string with appropriate separators
 */
export function formatPhone(phone: string | null | undefined, countryCode?: string): string {
  if (!phone) return '';

  // Remove all non-numeric characters except leading +
  const hasPlus = phone.startsWith('+');
  const cleaned = phone.replace(/\D/g, '');

  if (!cleaned) return phone;

  // If no country code provided, try to detect format
  let normalizedCountry = (countryCode || '').toLowerCase();
  if (normalizedCountry === 'united states') normalizedCountry = 'us';
  if (normalizedCountry === 'united kingdom') normalizedCountry = 'gb';
  if (normalizedCountry === 'india') normalizedCountry = 'in';
  if (normalizedCountry === 'australia') normalizedCountry = 'au';
  if (normalizedCountry === 'germany') normalizedCountry = 'de';
  if (normalizedCountry === 'france') normalizedCountry = 'fr';
  if (normalizedCountry === 'pakistan') normalizedCountry = 'pk';

  // Handle different country formats
  switch (normalizedCountry) {
    case 'us':
    case 'ca': // US/Canada: +1 (XXX) XXX-XXXX or XXX-XXX-XXXX
      return formatNorthAmerica(cleaned, hasPlus);

    case 'gb': // UK: +44 XXXX XXXXXX
      return formatUK(cleaned, hasPlus);

    case 'in': // India: +91 XXXXX XXXXX
      return formatIndia(cleaned, hasPlus);

    case 'au': // Australia: +61 X XXXX XXXX
      return formatAustralia(cleaned, hasPlus);

    case 'de': // Germany: +49 XXX XXXXXXX
      return formatGermany(cleaned, hasPlus);

    case 'fr': // France: +33 X XX XX XX XX
      return formatFrance(cleaned, hasPlus);

    case 'pk': // Pakistan: +92 XXX XXXXXXX
      return formatPakistan(cleaned, hasPlus);

    default:
      // Generic international format: group by 3-4 digits
      return formatGeneric(cleaned, hasPlus);
  }
}

function formatNorthAmerica(cleaned: string, hasPlus: boolean): string {
  // Remove leading 1 if present
  let digits = cleaned;
  if (cleaned.length === 11 && cleaned[0] === '1') {
    digits = cleaned.substring(1);
    hasPlus = true;
  }

  if (digits.length === 10) {
    return `${hasPlus ? '+1 ' : ''}(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
  } else if (digits.length > 6) {
    return `${digits.substring(0, 3)}-${digits.substring(3, 6)}-${digits.substring(6)}`;
  } else if (digits.length > 3) {
    return `${digits.substring(0, 3)}-${digits.substring(3)}`;
  }
  return digits;
}

// UK: +44 XXXX XXXXXX
function formatUK(cleaned: string, hasPlus: boolean): string {
  // Remove leading 44 if present
  let digits = cleaned;
  if (cleaned.startsWith('44')) {
    digits = cleaned.substring(2);
    hasPlus = true;
  }

  if (digits.length === 10) {
    return `${hasPlus ? '+44 ' : ''}${digits.substring(0, 4)} ${digits.substring(4)}`;
  }
  return formatGeneric(cleaned, hasPlus);
}

function formatIndia(cleaned: string, hasPlus: boolean): string {
  // Remove leading 91 if present
  let digits = cleaned;
  if (cleaned.startsWith('91')) {
    digits = cleaned.substring(2);
    hasPlus = true;
  }

  if (digits.length === 10) {
    return `${hasPlus ? '+91 ' : ''}${digits.substring(0, 5)} ${digits.substring(5)}`;
  }
  return formatGeneric(cleaned, hasPlus);
}

function formatAustralia(cleaned: string, hasPlus: boolean): string {
  // Remove leading 61 if present
  let digits = cleaned;
  if (cleaned.startsWith('61')) {
    digits = cleaned.substring(2);
    hasPlus = true;
  }

  if (digits.length === 9) {
    return `${hasPlus ? '+61 ' : ''}${digits.substring(0, 1)} ${digits.substring(1, 5)} ${digits.substring(5)}`;
  }
  return formatGeneric(cleaned, hasPlus);
}

function formatGermany(cleaned: string, hasPlus: boolean): string {
  // Remove leading 49 if present
  let digits = cleaned;
  if (cleaned.startsWith('49')) {
    digits = cleaned.substring(2);
    hasPlus = true;
  }

  if (digits.length >= 10) {
    return `${hasPlus ? '+49 ' : ''}${digits.substring(0, 3)} ${digits.substring(3)}`;
  }
  return formatGeneric(cleaned, hasPlus);
}

function formatFrance(cleaned: string, hasPlus: boolean): string {
  // Remove leading 33 if present
  let digits = cleaned;
  if (cleaned.startsWith('33')) {
    digits = cleaned.substring(2);
    hasPlus = true;
  }

  if (digits.length === 9) {
    return `${hasPlus ? '+33 ' : ''}${digits.substring(0, 1)} ${digits.substring(1, 3)} ${digits.substring(3, 5)} ${digits.substring(
      5,
      7,
    )} ${digits.substring(7)}`;
  }
  return formatGeneric(cleaned, hasPlus);
}

function formatPakistan(cleaned: string, hasPlus: boolean): string {
  // Remove leading 92 if present
  let digits = cleaned;
  if (cleaned.startsWith('92')) {
    digits = cleaned.substring(2);
    hasPlus = true;
  }

  if (digits.length === 10) {
    return `${hasPlus ? '+92 ' : ''}${digits.substring(0, 3)} ${digits.substring(3)}`;
  }
  return formatGeneric(cleaned, hasPlus);
}

// Generic fallback: split by groups of 3-4
function formatGeneric(cleaned: string, hasPlus: boolean): string {
  const prefix = hasPlus ? '+' : '';

  // Group digits: first 2-3 as country code, then groups of 3-4
  if (cleaned.length > 10) {
    // Likely has country code
    const countryCode = cleaned.substring(0, cleaned.length - 10);
    const remaining = cleaned.substring(cleaned.length - 10);
    return `${prefix}${countryCode} ${remaining.match(/.{1,3}/g)?.join(' ') || remaining}`;
  }

  // Just group by 3s
  return prefix + (cleaned.match(/.{1,3}/g)?.join(' ') || cleaned);
}
